<ng-container *ngIf="!success; else successTemplate">
  <div class="main-container" [@inOutAnimation]="animationState">
    <!-- Pass a nested fieldTree to a recursive template -->
    <form
      [formGroup]="form"
      (ngSubmit)="onSubmit()"
      *ngIf="!isLoading"
      class="form-container full-width"
    >
      <ng-container *ngFor="let field of fieldTree">
        <ng-container
          *ngIf="isGroupVisible(field.name)"
          [ngTemplateOutlet]="fieldTemplate"
          [ngTemplateOutletContext]="{ $implicit: field, formGroup: form }"
        ></ng-container>
      </ng-container>

      <div class="form-footer" *ngIf="isFormFooterVisible()" [@inOutAnimation]="'visible'">
        <app-notice
          [title]="'notice.human_verification.title' | translate"
          [body]="'notice.human_verification.body' | translate"
          icon="check"
          type="success"
        ></app-notice>

        <rm-button
          class="full-width submit-btn"
          [ngClass]="{ disabled: isSubmitting || form.invalid }"
          design="primary"
          [rounded]="true"
          type="submit"
          size="sm"
          [disabled]="isSubmitting || form.invalid"
          >{{ (isSubmitting ? 'form.submitting' : 'form.submit') | translate }}</rm-button
        >
      </div>
    </form>

    <!-- Recursive field template -->
    <ng-template #fieldTemplate let-field let-formGroup="formGroup">
      <!-- GROUP (group-raw): render nested form group -->
      <ng-container
        *ngIf="
          field.interface === 'group-raw' &&
          !(field.name === 'sources' || field.name === 'source_options_group')
        "
      >
        <div
          [formGroup]="formGroup.get(field.name)"
          class="group-container"
          [id]="field.name"
          [@inOutAnimation]="getGroupAnimationState(field.name)"
        >
          <!-- <h2>{{ field.name }}</h2> -->
          <ng-container *ngFor="let child of field.children">
            <ng-container
              *ngTemplateOutlet="
                fieldTemplate;
                context: { $implicit: child, formGroup: formGroup.get(field.name) }
              "
            ></ng-container>
          </ng-container>
        </div>
      </ng-container>

      <!-- NON-GROUP: render actual controls (wrapped with the current formGroup) -->
      <ng-container
        *ngIf="
          field.interface !== 'group-raw' && field.name !== 'is_verified' && field.name !== 'status'
        "
      >
        <ng-container *ngIf="field.name === 'report_type'">
          <!-- Only render this if the field is report_type -->
          <div class="report-type-container">
            <h1 class="page-title">{{ 'form.title' | translate }}</h1>
            <p class="page-hint">
              {{ 'form.hint' | translate }}
            </p>

            <!-- Anonymity & Security Notice -->
            <app-notice
              [title]="'form.anonymity_notice.title' | translate"
              [body]="'form.anonymity_notice.body' | translate"
              icon="shield"
              type="info"
              size="md"
            ></app-notice>

            <div
              [id]="field.name"
              [formGroup]="formGroup"
              [hidden]="isHidden(field, form)"
              class="full-width notice-gap"
            >
              <app-card-select
                [options]="reportTypeOptions"
                [columns]="2"
                [formControlName]="field.name"
              ></app-card-select>
            </div>

            <!-- Legal Disclaimer -->
            <p class="legal-disclaimer">
              {{ 'form.legal_disclaimer' | translate }}
            </p>
          </div>
        </ng-container>
        <ng-container *ngIf="field.name === 'congressional_district'">
          <!--TODO: Instead of hard-coding field name, use a dynamic approach by determining which collection to fetch options from-->
          <div
            [id]="field.name"
            [class]="
              'form-field' + (field.width?.startsWith('half') ? ' half-width' : ' full-width')
            "
            [hidden]="isHidden(field, form)"
            [formGroup]="formGroup"
          >
            <mat-label class="field-label">
              {{ getTranslatedLabel(field.label) | translate
              }}<span *ngIf="isRequired(field, form)" style="color: var(--danger-foreground)">{{
                ' *'
              }}</span>
            </mat-label>
            <mat-form-field appearance="fill" class="coordinate-field">
              <input [formControlName]="field.name" matInput readonly />
              <mat-error
                *ngIf="formGroup.get(field.name)?.hasError('noDistrict')"
                style="color: var(--danger-foreground)"
              >
                {{ 'form.errors.no_district_found' | translate }}
              </mat-error>
            </mat-form-field>
          </div>
        </ng-container>
        <ng-container
          *ngIf="field.name !== 'report_type' && field.name !== 'congressional_district'"
        >
          <ng-container *ngIf="field.name === 'location'">
            <h1 class="page-title">{{ formTitle | translate }}</h1>
            <p class="page-hint">
              {{ 'form.report_type_hint' | translate }}
            </p>
          </ng-container>
          <div
            [id]="field.name"
            [class]="
              'form-field' + (field.width?.startsWith('half') ? ' half-width' : ' full-width')
            "
            [hidden]="isHidden(field, form)"
            [formGroup]="formGroup"
          >
            <mat-label class="field-label">
              {{ getTranslatedLabel(field.label) | translate
              }}<span *ngIf="isRequired(field, form)" style="color: var(--danger-foreground)">{{
                ' *'
              }}</span>
            </mat-label>

            <!-- badge notice -->
            <ng-container>
              <app-notice
                size="sm"
                icon="info"
                *ngIf="notices.includes(field.name)"
                [title]="'notice.hints.' + field.name | translate"
              ></app-notice>
            </ng-container>

            <!-- input -->
            <ng-container *ngIf="field.interface === 'input'">
              <mat-form-field appearance="fill">
                <input
                  matInput
                  type="text"
                  [formControlName]="field.name"
                  [placeholder]="getPlaceholder(field, form) | translate"
                  [attr.maxlength]="getFieldMaxLength('short_input_max_length')"
                />
              </mat-form-field>
            </ng-container>

            <!-- input-multiline -->
            <ng-container *ngIf="field.interface === 'input-multiline'">
              <mat-form-field appearance="fill">
                <textarea
                  matInput
                  [formControlName]="field.name"
                  [placeholder]="getPlaceholder(field, form) | translate"
                  [attr.maxlength]="getFieldMaxLength('long_input_max_length')"
                  cdkTextareaAutosize
                  cdkAutosizeMinRows="2"
                  cdkAutosizeMaxRows="5"
                ></textarea>
              </mat-form-field>
            </ng-container>

            <!-- select-dropdown -->
            <ng-container *ngIf="field.interface === 'select-dropdown'">
              <mat-form-field appearance="fill">
                <mat-select
                  [name]="field.name"
                  [formControlName]="field.name"
                  [placeholder]="getPlaceholder(field, form) || 'form.dropdown.select' | translate"
                  (selectionChange)="
                    field.name === 'report_datetime_is_exact' ? setDate($event) : null
                  "
                >
                  <mat-option
                    *ngFor="let option of field.options['choices']"
                    [value]="option['value']"
                    [id]="'select-' + field.name + '-' + option['value']"
                  >
                    {{ option['text'] | translate }}
                  </mat-option>
                </mat-select>
              </mat-form-field>
            </ng-container>

            <!-- select-multiple-checkbox -->
            <ng-container
              *ngIf="field.interface === 'select-multiple-checkbox' && !!field.options?.choices"
            >
              <app-chips-select
                [options]="field.options['choices']"
                [allowOther]="field.options['allowOther'] ?? false"
                [formControlName]="field.name"
                [multiple]="true"
                [placeholder]="getPlaceholder(field, form) | translate"
              ></app-chips-select>
            </ng-container>

            <!-- select-radio -->
            <ng-container *ngIf="field.interface === 'select-radio' && !!field.options?.choices">
              <app-chips-select
                [options]="field.options['choices']"
                [allowOther]="field.options['allowOther'] ?? false"
                [formControlName]="field.name"
                [multiple]="false"
                [placeholder]="getPlaceholder(field, form) | translate"
              ></app-chips-select>
            </ng-container>

            <!-- datetime-picker -->
            <ng-container *ngIf="field.interface === 'datetime'">
              <mat-form-field appearance="fill" class="datetime">
                <input
                  matInput
                  [ngxMatDatetimePicker]="picker"
                  [formControlName]="field.name"
                  (focus)="picker.open()"
                  #input
                  placeholder="MM/DD/YYYY, h:mm A"
                  readonly
                />
                <mat-datepicker-toggle matSuffix [for]="$any(picker)"></mat-datepicker-toggle>
                <ngx-mat-datetime-picker
                  #picker
                  [showSeconds]="false"
                  (closed)="input.blur()"
                  [enableMeridian]="true"
                ></ngx-mat-datetime-picker>
              </mat-form-field>
            </ng-container>

            <!-- file uploader -->
            <ng-container *ngIf="field.interface === 'files'">
              <app-uploader
                [formControlName]="field.name"
                [title]="'form.upload_file.title' | translate"
                [subtitle]="'form.upload_file.subtitle' | translate"
                [hint]="'form.upload_file.hint' | translate"
                [multiple]="true"
                accept=".pdf,*.doc,*.docx,.txt,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/pdf,text/plain,video/*,image/*"
              ></app-uploader>
            </ng-container>

            <ng-container *ngIf="field.interface === 'map'">
              <!-- TODO: Add map. Current setup is placeholder only -->
              <app-location [parentForm]="form"></app-location>
            </ng-container>

            <mat-label *ngIf="field.note" class="field-hint">{{
              field.note | translate
            }}</mat-label>
          </div>
        </ng-container>
      </ng-container>

      <ng-container *ngIf="field.interface === 'group-raw' && field.name === 'verification'">
        <div
          [id]="field.name"
          [formGroup]="formGroup.get(field.name)"
          class="group-container"
          [@inOutAnimation]="getGroupAnimationState(field.name)"
        >
          <app-sms-verification formControlName="is_verified"></app-sms-verification>
        </div>
      </ng-container>

      <ng-container *ngIf="field.interface === 'group-raw' && field.name === 'sources'">
        <div
          class="group-container"
          [id]="field.name"
          [formGroup]="formGroup.get(field.name)"
          [@inOutAnimation]="getGroupAnimationState(field.name)"
        >
          <app-source-repeater
            [parentForm]="form"
            [fieldTree]="getGroupFromFieldTree(field.name)?.children ?? []"
            [sourceButtons]="sourceOptions"
          ></app-source-repeater>
        </div>
      </ng-container>
    </ng-template>
  </div>
</ng-container>

<ng-template #successTemplate>
  <div class="main-container success-container" [@inOutAnimation]="animationState">
    <div [ngClass]="['success-icon', showUrgentNotice ? 'urgent' : '']">
      <mat-icon icon class="material-symbols-outlined">check</mat-icon>
    </div>
    <div class="success-message">
      <h1>{{ 'form.success.title' | translate }}</h1>
      <p>{{ 'form.success.body' | translate }}</p>
    </div>
    <app-notice
      *ngIf="showUrgentNotice"
      [title]="'notice.urgent.title' | translate"
      [body]="'notice.urgent.body' | translate"
      icon="electric_bolt"
      type="urgent"
    ></app-notice>
    <app-notice
      [title]="'notice.next.title' | translate"
      [body]="'notice.next.body' | translate"
      icon="info"
      type="success"
    ></app-notice>
    <div class="success-buttons">
      <rm-button
        class="full-width"
        [design]="showUrgentNotice ? 'normal' : 'primary'"
        [rounded]="true"
        (click)="handleSubmitNew()"
        type="button"
      >
        <div class="button-contents">
          <mat-icon icon class="material-symbols-outlined">add_2</mat-icon>
          {{ 'form.submit_new' | translate }}
        </div>
      </rm-button>
      <rm-buttonlink
        class="full-width"
        [rounded]="true"
        [href]="appUrl"
        [openNewTab]="false"
        design="gray"
      >
        <div class="button-contents">
          {{ 'form.resistmap_link' | translate }}
          <mat-icon icon class="material-symbols-outlined">arrow_forward</mat-icon>
        </div>
      </rm-buttonlink>
      <p style="color: var(--hint-foreground); font-size: 13px">
        {{ 'notice.hints.submit_new' | translate }}
      </p>
    </div>
  </div>
</ng-template>
